import { func, IValidator } from 'validx'

// #region Range

/**
 * Validates that the value is inclusively between the bounds via validx.
 */
export function between(
  inclusiveMin: number,
  inclusiveMax: number,
  message?: string,
) {
  const msg =
    message ||
    `The value must be between ${inclusiveMin} and ${inclusiveMax} characters.`

  return func(
    ({ value }) => value >= inclusiveMin && value <= inclusiveMax,
    msg,
  )
}

/**
 * Validates that the value is greater than or equal to the specified lower bound
 * via validx.
 */
export function greaterThanOrEqual(lowerBound: number, message?: string) {
  const msg =
    message || `The value must be greater than or equal to ${lowerBound}`

  return func(({ value }) => value >= lowerBound, msg)
}

// #endregion

// #region Length Range

/**
 * Validates that the value has a length inclusively between the bounds specified via validx.
 */
export function lengthBetween(
  inclusiveMin: number,
  inclusiveMax: number,
  message?: string,
) {
  const msg =
    message ||
    `The value must be between ${inclusiveMin} and ${inclusiveMax} characters.`

  return func(({ value }) => {
    return value.length >= inclusiveMin && value.length <= inclusiveMax
  }, msg)
}

/**
 * Validates that the value has a length inclusively greater than the bound specified via validx.
 */
export function maxLength(inclusiveMax: number, message?: string) {
  const msg =
    message || `The value can't be greater than ${inclusiveMax} characters.`
  return func(({ value }) => {
    return value.length <= inclusiveMax
  }, msg)
}

/**
 * Validates that the value has a length inclusively less than the bound specified via validx.
 */
export function minLength(inclusiveMin: number, message?: string) {
  const msg =
    message || `The value can't be less than ${inclusiveMin} characters.`
  return func(({ value }) => {
    return value.length >= inclusiveMin
  }, msg)
}

// #endregion

/**
 * Validates all elements in the array where each element implements a `validation`
 * property of type validation context.
 * @param {*} message
 */
export function validateElements(message: string) {
  const msg = message || 'There are invalid elements'

  return func(({ value }) => {
    return (
      value
        .map((v: any) => {
          v.validation.reset()
          return v.validation.validate().isValid
        })
        .every(Boolean) || message
    )
  }, msg)
}

// #region Tax-form validators (mirrored from arphyx validx-util.ts so client
// behavior matches the customer-facing app for the same fields).

/**
 * Allows letters, numbers, space, dot, dash, comma, plus, ampersand, percent —
 * the character set tax forms accept. Empty values pass; pair with `required`
 * when presence is also required.
 */
export function mustBeValidTaxFormInputValue(
  message = 'Only letters, numbers and basic symbols are allowed',
) {
  return func(({ value }) => {
    const str = String(value ?? '')
    if (!str) return true
    return /^[a-zA-Z0-9 .\-,+&%]*$/.test(str.trim()) || message
  }, message)
}

/**
 * Validates an SSN or ITIN with mandatory dashes:
 * - SSN: XXX-XX-XXXX, blocking 000/666/9XX area numbers and reserved suffixes
 * - ITIN: 9XX-XX-XXXX (always starts with 9)
 */
export function mustBeValidPersonalTaxpayerIdentificationNumber(
  message = 'Please enter a valid SSN or ITIN',
) {
  return func(({ value }) => {
    const str = String(value ?? '')
    const ssn = /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/
    const itin = /^9\d{2}-\d{2}-(?!0000)\d{4}$/
    return ssn.test(str) || itin.test(str) || message
  }, message)
}

export function mustBeValidEin(message = 'EIN must be in format XX-XXXXXXX') {
  return func(({ value }) => {
    const str = String(value ?? '')
    if (!str) return true
    return /^\d{2}-\d{7}$/.test(str) || message
  }, message)
}

/**
 * Validates that a date is on or before `relativeTo` (defaults to now).
 * Accepts a `Date` or a string the JS Date constructor can parse (e.g., the
 * MM/DD/YYYY strings WP stores DOB as). Empty/invalid values are skipped here
 * so `required` can produce its own message.
 */
export function mustBeInThePast(
  message = 'The date must be in the past',
  relativeTo?: () => Date,
) {
  const ref = relativeTo ?? (() => new Date())
  return func(({ value }) => {
    if (value === null || value === undefined || value === '') return true
    const date = value instanceof Date ? value : new Date(String(value))
    if (Number.isNaN(date.getTime())) return message
    return date.getTime() <= ref().getTime() || message
  }, message)
}

/**
 * Validates a US phone number — exactly 10 digits after stripping the optional
 * country code, with flexible formatting (spaces, dashes, parens, dots).
 */
export function isValidUsPhoneNumber(
  message = 'Please enter a valid U.S. phone number',
) {
  return func(({ value }) => {
    const str = String(value ?? '')
    if (!str) return true
    const digitsOnly = str.replace(/^\+?1?/, '').replace(/\D/g, '')
    if (digitsOnly.length !== 10) return message
    return /^[+]?1?[\d\s\-().]+$/.test(str) || message
  }, message)
}

/**
 * Conditional validator wrapper: only runs `validator` when `condition()`
 * returns true. Use for rules like "region required only when country is US".
 */
export function when<T>(
  condition: () => boolean,
  validator: IValidator<T>,
): IValidator<T> {
  return (ctx) => (condition() ? validator(ctx) : true)
}

// #endregion
